import api from './client';
import type {
  OrgCreate,
  OrgUpdate,
  OrgResponse,
  OrgMemberAdd,
  ListParams,
} from './types';

export const organizationsApi = {
  /**
   * List all organizations
   * @param params - Query parameters (limit)
   */
  list: async (params?: ListParams): Promise<OrgResponse[]> => {
    const { data } = await api.get('/organizations', { params });
    return data;
  },

  /**
   * Get a specific organization by ID
   * @param orgId - Organization ID
   */
  get: async (orgId: string): Promise<OrgResponse> => {
    const { data } = await api.get(`/organizations/${orgId}`);
    return data;
  },

  /**
   * Create a new organization
   * @param org - Organization data
   */
  create: async (org: OrgCreate): Promise<OrgResponse> => {
    const { data } = await api.post('/organizations', org);
    return data;
  },

  /**
   * Update an existing organization
   * @param orgId - Organization ID
   * @param updates - Fields to update
   */
  update: async (orgId: string, updates: OrgUpdate): Promise<OrgResponse> => {
    const { data } = await api.patch(`/organizations/${orgId}`, updates);
    return data;
  },

  /**
   * Delete an organization
   * @param orgId - Organization ID
   */
  delete: async (orgId: string): Promise<void> => {
    await api.delete(`/organizations/${orgId}`);
  },

  /**
   * Add a member to an organization
   * @param orgId - Organization ID
   * @param memberData - Member data (user_id, role)
   */
  addMember: async (orgId: string, memberData: OrgMemberAdd): Promise<void> => {
    const { data } = await api.post(`/organizations/${orgId}/members`, memberData);
    return data;
  },

  /**
   * Remove a member from an organization
   * @param orgId - Organization ID
   * @param userId - User ID to remove
   */
  removeMember: async (orgId: string, userId: string): Promise<void> => {
    await api.delete(`/organizations/${orgId}/members/${userId}`);
  },

  /**
   * Upload logo for an organization
   * @param orgId - Organization ID
   * @param file - Image file to upload
   * @returns Object with logo_url and message
   */
  uploadLogo: async (orgId: string, file: File): Promise<{ logo_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post(`/organizations/${orgId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },
};

// WhatsApp-specific API
export const whatsappApi = {
  getConfig: async (): Promise<{ app_id: string; config_id: string }> => {
    const { data } = await api.get('/whatsapp/config');
    return data;
  },

  completeEmbeddedSignup: async (code: string): Promise<{
    status: string;
    business_name: string;
    display_phone: string;
    phone_number_id: string;
  }> => {
    const { data } = await api.post('/whatsapp/embedded-signup', { code });
    return data;
  },

  disconnect: async (): Promise<void> => {
    await api.post('/whatsapp/disconnect');
  },
};
